import { DisplayField, DisplayMode, SavedView } from '../types'
import { apiService } from './api.service'
import { documentService } from './document.service'

export interface SavedViewQueryParams {
  page?: number
  page_size?: number
  ordering?: string
}

class SavedViewService {
  private cachedViews: Map<number, SavedView> = new Map()

  async getCached(id: number): Promise<SavedView | undefined> {
    const cached = this.cachedViews.get(id)
    if (cached) {
      return cached
    }

    try {
      const response = await apiService.get<SavedView>(`saved_views/${id}/`, {
        params: {
          full_perms: true,
        },
      })
      const view = response.data
      this.cachedViews.set(id, view)
      return view
    } catch (error) {
      return undefined
    }
  }

  async setCached(id: number, view: SavedView): Promise<void> {
    this.cachedViews.set(id, view)
  }

  async getSavedViews(params?: SavedViewQueryParams): Promise<{ results: SavedView[]; count: number }> {
    const response = await apiService.get<{ results: SavedView[]; count: number }>('saved_views/', {
      params: {
        ...params,
        full_perms: true,
      },
    })
    return response.data
  }

  async getAllSavedViews(): Promise<SavedView[]> {
    const response = await apiService.get<{ results: SavedView[] } | SavedView[]>('saved_views/', {
      params: {
        full_perms: true,
      },
    })
    const data = response.data

    if (Array.isArray(data)) {
      return data
    } else if (data && typeof data === 'object' && 'results' in data) {
      return data.results
    } else {
      return []
    }
  }

  async createSavedView(savedView: Partial<SavedView>): Promise<SavedView> {
    const response = await apiService.post<SavedView>('saved_views/', savedView)
    return response.data
  }

  async updateSavedView(id: number, savedView: Partial<SavedView>): Promise<SavedView> {
    const response = await apiService.patch<SavedView>(`saved_views/${id}/`, savedView)
    return response.data
  }

  async updateManySavedViews(savedViews: SavedView[]): Promise<SavedView[]> {
    const promises = savedViews.map((view) => this.updateSavedView(view.id, view))
    return Promise.all(promises)
  }

  async deleteSavedView(id: number): Promise<void> {
    await apiService.delete(`saved_views/${id}/`)
  }

  getDisplayFields(t: (key: string) => string): DisplayField[] {
    return [
      { id: 'title', name: t('title') },
      { id: 'created', name: t('created') },
      { id: 'added', name: t('added') },
      { id: 'tag', name: t('tags') },
      { id: 'correspondent', name: t('correspondent') },
      { id: 'documenttype', name: t('document_type') },
      { id: 'workflowstatus', name: t('document_workflow.status') },
      { id: 'storagepath', name: t('storage_path') },
      { id: 'note', name: t('notes') },
      { id: 'owner', name: t('owner') },
      { id: 'shared', name: t('shared') },
      { id: 'asn', name: t('asn') },
      { id: 'pagecount', name: t('pages') },
    ]
  }

  getDisplayModes(t: (key: string) => string): { value: DisplayMode; label: string }[] {
    return [
      { value: DisplayMode.TABLE, label: t('table') },
      { value: DisplayMode.SMALL_CARDS, label: t('small_cards') },
      { value: DisplayMode.LARGE_CARDS, label: t('large_cards') },
      { value: DisplayMode.HIERARCHY, label: t('hierarchy') },
    ]
  }

  async getDocumentCount(view: SavedView): Promise<number> {
    try {
      const response = await documentService.listFiltered(1, 1, view.sort_field, view.sort_reverse, view.filter_rules, {
        fields: 'id',
        truncate_content: true,
      })
      return response.count || 0
    } catch (error) {
      return 0
    }
  }

  clearCache(): void {
    this.cachedViews.clear()
  }

  async loadDocumentCounts(views: SavedView[]): Promise<SavedView[]> {
    const viewsWithCounts = await Promise.all(
      views.map(async (view) => {
        const count = await this.getDocumentCount(view)
        return { ...view, document_count: count }
      })
    )
    return viewsWithCounts
  }
}

export const savedViewService = new SavedViewService()

export function sortSavedViewsByStoredOrder<T extends { id: number }>(views: T[], order: number[] | null | undefined): T[] {
  if (!order || order.length === 0) {
    return views
  }
  const known = order.map((id) => views.find((v) => v.id === id)).filter((v): v is T => Boolean(v))
  const knownIds = new Set(known.map((v) => v.id))
  const rest = views.filter((v) => !knownIds.has(v.id))
  return [...known, ...rest]
}
